import { createDeepSignal } from "@solid-primitives/resource";
import { cookieStorage, makePersisted } from "@solid-primitives/storage";
import { stAnalytics } from "@storytell/analytics";
import {
  type AuthService,
  type AuthStore,
  emptyRealm,
  firebaseConfig,
  getAuthIsGuest,
  getEmailTypeFromDomain,
  initialAuthStore,
  isLifecycleError,
  isLifecycleLoading,
  isLifecycleRegistered,
  type PostAuthAction,
} from "@storytell/auth";
import { useAuthNoOpMethods } from "@storytell/auth-noop-implementation";
import { dispatchGlobalEvent } from "@storytell/events";
import { type Logger, Named } from "@storytell/logger";
import { type FirebaseApp, initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { type Accessor, createEffect, createMemo, createResource, createSignal } from "solid-js";
import { getRequestEvent, isServer } from "solid-js/web";
import { BROWSER_STORAGE_KEY_OAUTH_REDIRECT_IN_PROGRESS, useAuthBrowserMethods } from "./authBrowserMethods";
import { clientAuthResourceServer } from "./authResourceHandlerClient";
import { authResourceHandlerServer } from "./authResourceHandlerServer";
import { clearSignedInCookie, setSignedInCookie } from "./redirectHintCookies";

// Auth service implementation for v3 web-app.
//
// It is designed to work both server-side and in the browser by leveraging
// different implementations of the firebase app.
//
// It's using a resource as the main store for the auth data so that it can trigger
// <Suspense> on the server and force it to wait until it's loaded before rendering the
// rest of the app and streaming it to the client.
//
// On the client the resource usually hydrates from the server render rather than
// fetching. It does fetch when this provider is mounted in the browser with no
// hydration payload: hot module reloading in dev, and a client-side navigation out of
// the wire-free (static) route group into (app). Otherwise the logic that runs on the
// client is the setupListeners() method that will watch for auth state changes and
// mutate the resource __without__ triggering a resource reload and causing a global
// loading state.
export const useAuthWithSSRService = (deps: {
  logger: Logger;
  deviceId: Accessor<string>;
  locale: Accessor<string>;
  userAgent: Accessor<string>;
}): AuthService => {
  const logger = new Named(deps.logger, "authSSR");
  const event = getRequestEvent();

  // Doing it like this to avoid initializing the app on the server
  let firebaseApp: FirebaseApp;
  const getFirebaseClientAuth = () => {
    if (!firebaseApp) {
      firebaseApp = initializeApp(firebaseConfig());
      return getAuth(firebaseApp);
    }
    return getAuth(firebaseApp);
  };

  const resource = createResource(
    (): Promise<AuthStore> =>
      isServer
        ? authResourceHandlerServer({
            event,
            logger,
            deviceId: deps.deviceId(),
            locale: deps.locale(),
            userAgent: deps.userAgent(),
          })
        : clientAuthResourceServer({
            logger,
            deviceId: deps.deviceId(),
            locale: deps.locale(),
            userAgent: deps.userAgent(),
            getFirebaseClientAuth,
          }),
    {
      deferStream: true,
      initialValue: initialAuthStore(),
      storage: createDeepSignal,
    }
  );

  const [data] = resource;

  // Check if we're in the middle of processing an OAuth redirect
  // This is used to show a loading state on auth pages when returning from Google OAuth
  const [isProcessingOAuthRedirect, setIsProcessingOAuthRedirect] = createSignal(
    !isServer && sessionStorage.getItem(BROWSER_STORAGE_KEY_OAUTH_REDIRECT_IN_PROGRESS) === "true"
  );

  // Watch for changes to the OAuth redirect flag (cleared when processOAuthRedirect completes)
  if (!isServer) {
    const checkOAuthFlag = () => {
      const flag = sessionStorage.getItem(BROWSER_STORAGE_KEY_OAUTH_REDIRECT_IN_PROGRESS);
      setIsProcessingOAuthRedirect(flag === "true");
    };

    // Check periodically until flag is cleared (processOAuthRedirect is async)
    if (isProcessingOAuthRedirect()) {
      const interval = setInterval(() => {
        checkOAuthFlag();
        if (!sessionStorage.getItem(BROWSER_STORAGE_KEY_OAUTH_REDIRECT_IN_PROGRESS)) {
          clearInterval(interval);
        }
      }, 100);
    }
  }

  // --- UI ONLY STATES ---

  // isLoading is a UI only state that returns true when we are attempting to
  // determine the auth state of the user.
  const isLoading = () => isLifecycleLoading(data().state);

  // isError is a UI only state that returns true when we failed to determine
  // the authentication state
  const isError = () => isLifecycleError(data().state);

  // isConnecting is a UI only state that returns true when the system is
  // loading the auth state or when we have experienced an error. In this state
  // the application is not usable.
  const isConnecting = () => isLoading() || isError();

  // isReady returns true if the auth service is ready to be used (e.g., we
  // are no longer connecting)
  const isReady = () => !isConnecting();

  // --- USER STATES ---

  // isGuest returns true when the user is unidentified.
  const isGuest = () => getAuthIsGuest(data());

  // isRegistered returns true when a user is identified and their email address
  // has been verified by the platform or the authentication provider.
  const isRegistered = () => isLifecycleRegistered(data().state);

  // --- HELPFUL ACCESSORS ---

  // realm returns the user's working context.
  const realm = () => data().authenticatedUser?.realm ?? emptyRealm();

  // authenticatedUser returns the authenticated user object which wraps
  // the user and has additional information about the last auth-sync operation
  const authenticatedUser = () => data().authenticatedUser;

  // user returns the user object which is common to all user operations.
  // This is a convenience method to avoid having to call authenticatedUser()
  // and then reference the embedded user.
  const user = () => data().authenticatedUser?.user;

  const userId = () => data()?.authenticatedUser?.user?.id as `user_${string}` | undefined;

  // token returns the active bearer token for the user.
  const token = () => data().token;
  // The server evaluates a memo once, before auth resolves, so it would stay false for the render.
  const hasToken = isServer ? () => !!data().token : createMemo(() => !!data().token);

  // Actions that storytell should perform automatically after a successful sign-in/up
  // Like taking a user back to refilling their credits after they got redirected to auth
  // when trying to do so from a guest account.
  const [postAuthAction, setPostAuthAction] = makePersisted(createSignal<PostAuthAction>(), {
    storage: cookieStorage,
    name: "authRedirect:v1",
    storageOptions: {
      path: "/",
      sameSite: "Lax",
      // Read on every write, so the 15 minutes run from the write rather than
      // from boot — a Date fixed here is already past for a long-open tab and
      // the browser drops the cookie. Max-Age would do the same, but these
      // options are carried into the delete, where it would outrank the epoch
      // expiry and clearing would stop clearing.
      get expires() {
        return new Date(Date.now() + 15 * 60 * 1000);
      },
    },
  });

  const methods = isServer
    ? useAuthNoOpMethods()
    : useAuthBrowserMethods({
        logger: deps.logger,
        resource,
        deviceId: deps.deviceId,
        locale: deps.locale,
        userAgent: deps.userAgent,
      });

  const signOut = () => {
    dispatchGlobalEvent("sign-out", undefined);
    setTimeout(() => {
      methods.signOut().catch((error: unknown) => {
        logger.error("sign_out_failed", "signing out failed", error);
      });
    }, 100);
    return Promise.resolve();
  };

  methods.setupListeners();

  // Mirror auth state into a non-HttpOnly cookie so the marketing landing route can
  // SSR-redirect signed-in users to /app without a flash of the homepage.
  // The (app) layout still owns auth enforcement; this cookie is just a hint.
  createEffect(() => {
    if (isServer) return;
    if (isRegistered()) {
      setSignedInCookie();
      // Persisted across logout (intentionally not cleared on signOut) so the
      // marketing header can offer returning users a sign-in instead of a
      // sign-up CTA — see ENG-6479.
      document.cookie = "hasSignedIn=1; path=/; max-age=31536000; samesite=lax";
    } else if (isReady()) {
      clearSignedInCookie();
    }
  });

  createEffect(() => {
    if (isRegistered()) {
      const u = user();
      if (!u) return;

      try {
        void stAnalytics.identify(u.id, {
          name: u.displayName,
          email: u.primaryEmail,
          emailType: getEmailTypeFromDomain(u.primaryEmail),
        });
      } catch (_error) {
        // todo: capture with sentry but don't allow errors to impact user
      }
    }
  });

  return {
    userId,
    isGuest,
    isReady,
    realm,
    authenticatedUser,
    isRegistered,
    isProcessingOAuthRedirect,
    user,
    token,
    hasToken,
    isConnecting,
    postAuthAction,
    setPostAuthAction,

    methods: {
      ...methods,
      signOut,
    },
  };
};
